import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DOCUMENT,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {DcpMedia} from '@app/model/dcp-media';
import {DcpComponentClassifications, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {CmsComponent} from '@spartacus/core';
import {CmsComponentData, CurrentProductService} from '@spartacus/storefront';
import {Observable} from 'rxjs';
import {DcpProductDataService} from '../../data/dcp-product-data.service';
import {DcpProductUtilitiesService} from '../../services/dcp-product-utilities.service';
import {ProductPageDecoratorService} from '../../services/product-page-decorator.service';
import {NavigationLink} from '../navigation-bar/navigation-link';
import {ProductDetailsNavigationLinkService} from '../navigation-bar/product-details-navigation-link.service';
import {ProductDetailsSectionComponent} from '../product-details-section/product-details-section.component';
import {ProductNameDisplayComponent} from '../navigation-bar/product-name-display/product-name-display.component';
import {HeroReviewComponent} from './hero-review/hero-review.component';
import {LimitedTextComponent} from '../../../shared/limited-text/limited-text.component';
import {CtaFormComponent} from '../../cta-form/cta-form.component';
import {ClassificationsTemplateComponent} from '../classifications-template/classifications-template.component';
import {DcpMediaComponent} from '../../../shared/dcp-media/dcp-media.component';

export interface ProductHeroComponent extends CmsComponent {
  classificationsProductData?: DcpComponentClassifications[];
}

@UntilDestroy()
@Component({
  selector: 'dcp-hero',
  templateUrl: './hero.component.html',
  styleUrls: ['./hero.component.scss'],
  imports: [
    ProductNameDisplayComponent,
    HeroReviewComponent,
    LimitedTextComponent,
    CtaFormComponent,
    ClassificationsTemplateComponent,
    DcpMediaComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HeroComponent extends ProductDetailsSectionComponent implements OnInit, OnDestroy {
  componentData$: Observable<ProductHeroComponent>;
  productClassifications: DcpComponentClassifications[];
  summary: string;
  description: string;
  gallery: DcpMedia[];
  galleryIndex: number;
  navLink: NavigationLink = {
    headline: 'productDetails.atAGlance',
    id: 'hero-section',
  };

  constructor(
    @Inject(DOCUMENT) private document: Document,
    public productPageDecoratorService: ProductPageDecoratorService,
    protected productDetailsNavigationLinkService: ProductDetailsNavigationLinkService,
    protected currentProductService: CurrentProductService,
    protected changeDetection: ChangeDetectorRef,
    private cmsData: CmsComponentData<ProductHeroComponent>,
    private productUtilitiesService: DcpProductUtilitiesService,
    private jsonService: JsonService,
    private renderer: Renderer2,
    private productDataService: DcpProductDataService,
  ) {
    super(
      currentProductService,
      changeDetection,
      productDetailsNavigationLinkService,
      productUtilitiesService,
      productPageDecoratorService,
    );
  }

  get hasContent(): boolean {
    return !!this.summary && !!this.description;
  }

  isVisible(): boolean {
    return !!this.hasContent || this.gallery?.length > 0;
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.componentData$ = this.cmsData.data$;
    this.fetchComponentData();
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  fetchComponentData() {
    this.componentData$.pipe(untilDestroyed(this)).subscribe((componentData) => {
      if (componentData?.classificationsProductData) {
        this.productClassifications = this.jsonService.parseSafely(
          componentData?.classificationsProductData?.toString(),
        ) as DcpComponentClassifications[];
      }
    });
  }

  /**
   * Supply all data needed for the template
   */
  prepareData(): void {
    if (this.product) {
      this.addCanonicalLink();
      this.summary = this.product.summary;
      this.description = this.product.description;
      this.prepareGallery();
    }
  }

  /**
   * Add canonical link to <head> of PDP (only for products with a target URL).
   * Guards against duplicates — safe to call on every prepareData() cycle.
   */
  addCanonicalLink(): void {
    const pdpTargetUrl = this.productUtilitiesService.getPdpTargetUrl(this.product);
    if (!pdpTargetUrl) {
      return;
    }
    const existing = this.document.head.querySelector('link[rel="canonical"]');
    if (existing) {
      this.renderer.setAttribute(existing, 'href', pdpTargetUrl);
      return;
    }
    const link: HTMLLinkElement = this.renderer.createElement('link');
    this.renderer.setAttribute(link, 'rel', 'canonical');
    this.renderer.setAttribute(link, 'href', pdpTargetUrl);
    this.renderer.appendChild(this.document.head, link);
  }

  /**
   * Data for the media gallery
   */
  prepareGallery(): void {
    this.gallery = this.productDataService.getMedia(this.product.data || []).filter((m) => m.type === 'GALLERY');
    this.galleryIndex = 0;
  }

  showNextGalleryItem() {
    if (this.gallery?.length) {
      this.galleryIndex = (this.galleryIndex + 1) % this.gallery.length;
    }
  }
  showPreviousGalleryItem(): void {
    if (this.gallery?.length) {
      this.galleryIndex = (this.galleryIndex - 1 + this.gallery.length) % this.gallery.length;
    }
  }
}
